import React from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { maskCEP, maskCNPJ, maskCPF, maskDate } from '@utils/masks';

interface IClinica {
  id_clinicas_credenciadas: number;
  contrato: string;
  nome_comercial: string;
  razao_social: string;
  cpf_cnpj: string;
  dias_funcionamento: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  nome_med: string;
  crm: string;
  crm_estado: string;
  trabalho_agendamento: string;
  pagamento_adiantado: string;
  telemedicina: string;
  observacao: string;
}

interface IAgendamento {
  cidade: string;
  cpf_cnpj_comer: string;
  cpf_colaborador: string;
  data_agendamento: string;
  estado: string;
  horario: string;
  id_agendamento: number;
  nome: string;
  nome_comercial: string;
  num_agenda: string;
  observacao: string;
  ordem_chegada: string;
  status: string;
  telemedicina: string;
  tipo_aso: string;
  usuario: string;
}

interface IColaborador {
  id_colaboradores: number;
  matricula_empresa: string;
  matricula_esocial: string;
  nome_completo: string;
  cpf_colaborador: string;
  rg: string;
  data_nascimento: string;
  genero: string;
  cnh: string;
  email: string;
  telefone: string;
  situacao: string;
  nis: string;
  funcao: string;
  setor: string;
  turno: string;
  cnpj: string;
  razao_social: string;
  data_admissao: string;
  horario_trab_entrada: string;
  horario_trab_saida: string;
  local_obra: string;
  cod_ghe: string;
  ghe: string;
}

interface IEmpresaColab {
  bairro: string;
  cep: string;
  cidade: string;
  complemento: string;
  estado: string;
  numero: string;
  rua: string;
}

interface ITelefoneEmpresa {
  cnpj: string;
  id_empresa_telefone: number;
  telefone: string;
}

interface IExameGHE {
  id_exame_ghe: number;
  cod_ghe: string;
  exame_empresa: string;
  dias: string;
  data: [
    {
      data_agendamento: string;
    }
  ];
}

interface IGhe {
  cod_ghe: string;
  cod_obra: string;
  crm: string;
  crm_estado: string;
  exibir: string;
  id_cad_ghe: number;
  nome_ghe: string;
  nome_med: string;
  obs_aso: string;
  tipo_plano: string;
  valor_plano: string;
}

interface IRisco_Ghe {
  id_risco_ghe: number;
  cod_ghe: string;
  nome: string;
}

export interface Credenciado {
  cpf_cnpj_comer: string;
  data_agendamento: string;
  exames: ExameCredenciado[];
  horario: string;
  id_exame_hibrido: number;
  linkDoatendimento: string;
  nome_comercial: string;
  num_agenda: string;
  ordem_chegada: string;
  tipo: string;
  tipo_agendamento: string;
}

export interface ExameCredenciado {
  exame_credenciado: any;
  exame_empresa: string;
  valor_credenciado: string;
}

interface IPdf {
  clinica: IClinica[];
  agendamento: IAgendamento[];
  colaborador: IColaborador[];
  exames_ghe: IExameGHE[];
  ghe: IGhe[];
  risco_ghe: IRisco_Ghe[];
  empresa_colab: IEmpresaColab[];
  telefone_empresa: ITelefoneEmpresa[];
  exames_hibrido: Credenciado[];
}

export const PDFHibrido = (data: IPdf[], page: string) => {
  function selectCpfOrCnpj(cpfOrCnpj: string) {
    if (cpfOrCnpj.length <= 11) {
      return maskCPF(cpfOrCnpj);
    } else {
      return maskCNPJ(cpfOrCnpj);
    }
  }

  // Logica de Riscos
  const numberOfColumnsRiscos = 2;
  const numberOfRowsRiscos = Math.ceil(
    data[0].risco_ghe.length / numberOfColumnsRiscos
  );

  const leftColumnRiscos = [];
  const rightColumnRiscos = [];
  const leftLengthRiscos = [];

  for (let i = 0; i < numberOfRowsRiscos; i++) {
    leftColumnRiscos[i] = data[0].risco_ghe[i * numberOfColumnsRiscos];
    rightColumnRiscos[i] = data[0].risco_ghe[i * numberOfColumnsRiscos + 1];
    leftLengthRiscos[i] = i;
  }

  const rightRiscos = rightColumnRiscos.map((right) => {
    if (right !== undefined) {
      return [{ text: right?.nome ? right?.nome : '', colSpan: 3 }, {}, {}];
    } else {
      return [{ text: '', colSpan: 3 }, {}, {}];
    }
  });
  
  const leftRiscos = leftColumnRiscos.map((left) => {
    if (left !== undefined) {
      return [{ text: left?.nome ? left?.nome : '', colSpan: 3 }, {}, {}];
    } else {
      return [{ text: '', colSpan: 3 }, {}, {}];
    }
  });

  const countRiscos = leftLengthRiscos.map((a, i) => [
    ...leftRiscos[i],
    ...rightRiscos[i]
  ]);

  // Logica para exames hibridos
  const examesHibridosFormatados = data[0].exames_hibrido.flatMap(item => {
    return item.exames.map(exame => ({
      nome: exame.exame_empresa, // Usando exame_empresa como nome do exame
      data: item.data_agendamento // Data do agendamento do item principal
    }));
  });

  // Dividir em duas colunas
  const numberOfColumns = 2;
  const numberOfRows = Math.ceil(examesHibridosFormatados.length / numberOfColumns);

  const leftColumn = [];
  const rightColumn = [];

  for (let i = 0; i < numberOfRows; i++) {
    leftColumn[i] = examesHibridosFormatados[i * numberOfColumns];
    rightColumn[i] = examesHibridosFormatados[i * numberOfColumns + 1];
  }

  const rowsExames = [];

  for (let i = 0; i < numberOfRows; i++) {
    const left = leftColumn[i];
    const right = rightColumn[i];
    
    const row = [
      // Coluna esquerda
      {
        text: left?.nome || '',
        colSpan: 2,
        border: [true, true, false, true]
      },
      {},
      // Data coluna esquerda
      {
        text: left?.data ? maskDate(left.data) : '',
        border: [false, true, true, true],
        bold: true
      },
      // Coluna direita
      {
        text: right?.nome || '',
        colSpan: 2,
        border: [true, true, false, true]
      },
      {},
      // Data coluna direita
      {
        text: right?.data ? maskDate(right.data) : '',
        border: [false, true, true, true],
        bold: true
      }
    ];
    
    rowsExames.push(row);
  }

  pdfMake.vfs = pdfFonts.pdfMake.vfs;

  const reportTitle = [
    {
      text: '',
      fontSize: 15,
      bold: true,
      margin: [15, 30, 0, 45]
    }
  ];

  const details = [
    {
      style: { fontSize: 10 },
      color: '#444',
      table: {
        widths: [80, 80, 60, 100, '*', 60],
        headerRows: 2,
        body: [
          [
            {
              text: [
                {
                  text: String('ATESTADO DE SAÚDE OCUPACIONAL').toLocaleUpperCase(),
                  style: 'tableHeader',
                  fontSize: 18,
                  alignment: 'center'
                },
                {
                  text: '\n',
                  alignment: 'center'
                },
                {
                  text: '\n(11) 96621 8866 - www.aryz.com.br - agendamento@aryz.com.br ',
                  alignment: 'center'
                }
              ],
              colSpan: 6,
              color: '#6D539E'
            },
            {},
            {},
            {},
            {},
            {}
          ],
          [
            {
              text: 'Dados da Empresa',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            },
            {},
            {},
            {},
            {},
            {}
          ],
          [
            {
              text: 'Empresa',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0].colaborador[0]?.razao_social, colSpan: 2 },
            {},
            {
              text: 'CNPJ',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.cnpj
                ? selectCpfOrCnpj(data[0]?.colaborador[0]?.cnpj)
                : '',
              colSpan: 2
            },
            {}
          ],
          [
            {
              text: 'Endereço',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: `${data[0]?.empresa_colab[0]?.rua}, ${data[0]
                ?.empresa_colab[0]?.numero}, ${data[0]?.empresa_colab[0]
                ?.complemento}, ${data[0]?.empresa_colab[0]?.bairro} - ${data[0]
                ?.empresa_colab[0]?.estado}, ${data[0]?.empresa_colab[0]
                ?.cidade}, ${
                data[0].empresa_colab[0]?.cep
                  ? maskCEP(data[0].empresa_colab[0]?.cep)
                  : ''
              }`,
              colSpan: 2
            },
            {},
            {
              text: 'Tel',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0].telefone_empresa[0]?.telefone, colSpan: 2 },
            {}
          ],
          [
            {
              text: 'Dados do Colaborador',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          [
            {
              text: 'Colaborador',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.nome_completo
                ? data[0].colaborador[0]?.nome_completo
                : '',
              colSpan: 5
            }
          ],
          [
            {
              text: 'Setor',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.setor
                ? data[0].colaborador[0]?.setor
                : ''
            },
            {
              text: 'Função',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.funcao
                ? data[0].colaborador[0]?.funcao
                : '',
              colSpan: 3
            }
          ],
          [
            {
              text: 'CPF',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.cpf_colaborador
                ? data[0].colaborador[0]?.cpf_colaborador
                : ''
            },
            {
              text: 'RG ou CTPS',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.rg
                ? data[0].colaborador[0]?.rg
                : '',
              colSpan: 3
            }
          ],
          [
            {
              text: 'Nascimento',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            {
              text: data[0].colaborador[0]?.data_nascimento
                ? maskDate(data[0].colaborador[0]?.data_nascimento)
                : ''
            },
            {
              text: 'GHE',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0]?.ghe[0]?.nome_ghe },
            {
              text: 'Sexo',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0]?.colaborador[0]?.genero }
          ],
          [
            {
              text: 'Dados do Agendamento',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          [
            {
              text: 'Resp. do PCMSO',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center',
              fontSize: 9
            },
            { text: data[0]?.ghe[0]?.nome_med, colSpan: 3 },
            {},
            {},
            {
              text: 'CRM',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: `${data[0]?.ghe[0]?.crm}/${data[0]?.ghe[0]?.crm_estado}` }
          ],
          [
            {
              text: 'Tipo de ASO',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0]?.agendamento[0]?.tipo_aso, colSpan: 5 }
          ],
          [
            {
              text: 'Riscos Ocupacionais',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          ...countRiscos,
          [
            {
              text: 'Exames Complementares Realizados',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          ...rowsExames,
          [
            {
              text: 'Observações do ASO',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          [
            {
              text: data[0].ghe[0]?.obs_aso ? data[0].ghe[0]?.obs_aso : '',
              colSpan: 6,
              rowSpan: 3
            }
          ],
          [{}, {}, {}, {}, {}, {}],
          [{}, {}, {}, {}, {}, {}],
          [
            {
              text: 'Aprovação e Assinatura',
              colSpan: 6,
              style: 'tableHeader',
              alignment: 'center',
              fillColor: '#6D539E',
              color: '#FFF',
              bold: true
            }
          ],
          [
            {
              rowSpan: 3,
              text: 'Data do ASO',
              alignment: 'center',
              margin: [5, 12, 10, 0],
              fillColor: '#88CBC5',
              color: '#000',
              bold: true
            },
            {
              rowSpan: 3,
              text: '___/___/___',
              alignment: 'center',
              width: 10,
              margin: [5, 12, 10, 0]
            },
            {
              rowSpan: 3,
              colSpan: 2,
              text: [{ text: 'Resultado' }, { text: '\n(indicar com um "X")' }],
              margin: [5, 5, 10, 0],
              alignment: 'center',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true
            },
            {},
            { text: 'APTO', alignment: 'center', fontSize: 8 },
            {}
          ],
          [
            {},
            {},
            {},
            {},
            { text: 'APTO C/ RESTRIÇÕES', alignment: 'center', fontSize: 8 },
            {}
          ],
          [
            {},
            {},
            {},
            {},
            { text: 'INAPTO', alignment: 'center', fontSize: 8 },
            {}
          ],
          [
            { text: '\n\n\n', colSpan: 3 },
            {},
            {},
            { text: '\n\n\n', colSpan: 3 },
            {},
            {}
          ],
          [
            {
              text: 'Médico Examinador',
              colSpan: 3,
              alignment: 'center',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true
            },
            {},
            {},
            {
              text: [
                { text: 'Colaborador' },
                { text: '\nDECLARO TER RECEBIDO UMA VIA DESTE ATESTADO' }
              ],
              colSpan: 3,
              alignment: 'center',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true
            },
            {},
            {}
          ]
        ]
      }
    }
  ];

  function Rodape(currentPage: string, pageCounf: string) {
    return [
      {
        text: `${currentPage} / ${pageCounf}`,
        alignment: 'right',
        fontSize: 9,
        margin: [0, 10, 20, 0]
      }
    ];
  }

  const docDefinitions: any = {
    info: {
      title: data[0].colaborador[0]?.nome_completo,
      subject: data[0].colaborador[0]?.nome_completo,
      creator: data[0].colaborador[0]?.nome_completo,
      producer: data[0].colaborador[0]?.nome_completo,
      keywords: data[0].colaborador[0]?.nome_completo,
      download: data[0].colaborador[0]?.nome_completo,
      filename: data[0].colaborador[0]?.nome_completo,
      downloadName: data[0].colaborador[0]?.nome_completo
    },
    pageSize: 'A4',
    pageMargins: [50, 50, 50, 40],
    header: [reportTitle],
    content: [details],
    fontSize: 5,
    footer: Rodape
  };

  pdfMake
    .createPdf(docDefinitions)
    .download(`ASO ARYZ - ${data[0].colaborador[0]?.nome_completo.replace('.',' ')}`);

  return <div />;
};